import * as React from "react";
import {
  makeStyles,
  Textarea,
  Button,
  Field,
  Switch,
  Input,
  Spinner,
  buttonClassNames,
  tokens,
  Label,
  SpinButton,
  Text,
} from "@fluentui/react-components";
import { CheckmarkCircle16Filled, ErrorCircle16Filled } from "@fluentui/react-icons";

/**
 * Interface définissant les propriétés du composant StandardisationPage.
 */
interface GeneralAssitantProps {
  handleMenuReload: () => void; // Fonction pour recharger le menu principal
  askAndExecuteGeneralRequest: (contextIsSelection: boolean, request: string) => void; // Fonction pour exécuter une requête générale
}

/**
 * Composant GeneralAssistantPage : permet à l'utilisateur d'envoyer des requêtes générales à une IA
 * en fonction du contexte (document entier ou sélection).
 * @param {GeneralAssitantProps} props - Propriétés du composant.
 * @returns {JSX.Element} - Le composant React.
 */
const GeneralAssistantPage: React.FC<GeneralAssitantProps> = (props: GeneralAssitantProps) => {
  // États locaux du composant
  const styles = useStyles();
  const [isOnlySelection, setIsOnlySelection] = React.useState(true); // Détermine si seule une sélection est utilisée
  const [errors, setErrors] = React.useState(""); // Message d'erreur pour les requêtes
  const [request, setRequest] = React.useState(""); // Contenu de la requête utilisateur
  const [isDisabled, setIsDisabled] = React.useState(true); // Désactive le bouton si la requête est invalide
  const [errorMessage, setErrorMessage] = React.useState<string>(""); // Message d'erreur pour la validation de l'entrée

  // État pour gérer l'animation de chargement
  const [loadingState, setLoadingState] = React.useState<LoadingState>("initial");
  type LoadingState = "initial" | "loading" | "loaded";

  // Contenu et icône du bouton en fonction de l'état de chargement
  const buttonContent =
    loadingState === "loading" ? "En cours..." : loadingState === "loaded" ? "Terminé" : "Envoyer";
  const buttonIcon =
    loadingState === "loading" ? (
      <Spinner size="tiny" />
    ) : loadingState === "loaded" ? (
      <CheckmarkCircle16Filled className={styles.successText} />
    ) : null;

  /**
   * Gère les changements dans le champ de texte.
   * Valide l'entrée utilisateur et affiche un message d'erreur si nécessaire.
   * @param {React.ChangeEvent<HTMLInputElement>} e - L'événement de changement.
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value.trim() === "") {
      setErrorMessage(
        "Votre entrée est vide. L'entrée ne peut pas être vide ni contenir moins de 7 caractères."
      );
      setIsDisabled(true);
    } else if (value.trim().length < 7) {
      setErrorMessage(
        "Veuillez entrer au moins 7 caractères. L'entrée ne peut pas être vide ni contenir moins de 7 caractères."
      );
      setIsDisabled(true);
    } else {
      setErrorMessage("");
      setIsDisabled(false);
    }
  };

  /**
   * Envoie une requête générale à l'IA.
   * Valide la requête avant de l'envoyer et gère les erreurs ou succès.
   */
  const askGeneralRequest = async () => {
    if (!request) {
      setErrors("Veuillez écrire votre question.");
    } else if (request.length < 7) {
      setErrors("S'il vous plait écrivez une question claire et détaillée.");
    } else {
      setErrors("");
      setLoadingState("loading");
      const generalReqStatus: any = await props.askAndExecuteGeneralRequest(
        isOnlySelection,
        request
      );
      if (generalReqStatus == -1) {
        setLoadingState("initial");
        setErrors("Crédits insuffisants.");
      } else {
        setLoadingState("loaded");
        setTimeout(() => {
          setLoadingState("initial");
          props.handleMenuReload(); // Trigger FullMenu reload after operation
        }, 1200);
      }
    }
  };

  /**
   * Gère le changement du type de sélection (document entier ou sélection spécifique).
   * @param {any} ev - L'événement de changement.
   */
  const onChangeSelectionType = React.useCallback(
    (ev: any) => {
      setIsOnlySelection(ev.currentTarget.checked);
    },
    [setIsOnlySelection]
  );

  // Rendu du composant
  return (
    <div className={styles.container}>
      {/* Titre et description du composant */}
      <p className={styles.text}>Assistant Excel Général</p>
      <p className={styles.textDescription}>
        {isOnlySelection
          ? "L'IA prendera votre document entier en contexte pour répondre à vos questions"
          : "L'IA tiendra compte des cases mentionnées dans vos questions pour vous répondre"}
      </p>
      {/* Champ de saisie pour la question */}
      <Field label="Requête" className={styles.fieldPadding}>
        <Textarea
          placeholder="Dans la case C5, calculez les profits de la journee. Copiez cela jusqu'a la case C20"
          size="medium"
          resize="vertical"
          rows={6}
          onChange={(e: any) => {
            setRequest(e.target.value);
            handleChange(e);
          }}
        />
        {/* Message d'erreur */}
        {errorMessage && (
          <div className={styles.errorText}>
            <ErrorCircle16Filled style={{ marginRight: "8px" }} />
            <Text>{errorMessage}</Text>
          </div>
        )}
      </Field>
      {/* Bouton pour sélectionner la case cible */}
      <Field className={styles.fieldAlignLeft}>
        <Switch
          checked={isOnlySelection}
          onChange={onChangeSelectionType}
          label={isOnlySelection ? "Document entier" : "Sélection du contexte"}
        />
        {/* Bouton submit */}
        <Button
          appearance="primary"
          disabled={isDisabled}
          className={`${styles.greenButton}`}
          onClick={askGeneralRequest}
          size="medium"
          icon={buttonIcon}
        >
          {" "}
          {buttonContent}
        </Button>
      </Field>
      {/* Message d'erreur */}
      {errors && (
        <div className={styles.errorText}>
          <ErrorCircle16Filled style={{ marginRight: "8px" }} />
          <Text>{errors}</Text>
        </div>
      )}
    </div>
  );
};

export default GeneralAssistantPage;

const useStyles = makeStyles({
  container: {
    padding: "20px",
    textAlign: "center",
    flexDirection: "column",
  },
  text: {
    fontSize: "24px",
    fontWeight: "bold",
  },
  textDescription: {
    fontSize: "12px",
    fontWeight: "bold",
  },
  fieldPadding: {
    paddingBottom: "12px",
  },
  fieldAlignLeft: {
    paddingBottom: "12px",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
    justifyContent: "flex-start",
    gap: "8px",
  },
  fieldFlexColumn: {
    display: "flex",
    flexDirection: "row",
    textAlign: "center",
    justifyContent: "center",
    alignItems: "center",
    gap: "4px",
    paddingBottom: "12px",
  },
  buttonAutoWidth: {
    width: "auto",
    height: "auto",
    padding: "8px 16px",
  },
  buttonNonInteractive: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
    color: tokens.colorNeutralForeground1,
    cursor: "default",
    pointerEvents: "none",

    [`& .${buttonClassNames.icon}`]: {
      color: tokens.colorStatusSuccessForeground1,
    },
  },
  errorText: {
    color: "red",
    display: "flex",
    alignItems: "center",
  },
  successText: {
    color: "green",
    display: "flex",
    alignItems: "center",
  },
  greenButton: {
    backgroundColor: "#217346",
    color: "white",
    "&:hover": {
      backgroundColor: "#1e6d43",
    },
  },
});
